// client/src/pages/admin/AdminWholesalerDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  ArrowLeft, Building, Mail, Phone, MapPin, Calendar,
  ShoppingCart, Ban, CheckCircle, Edit, Save, X, Award, Hash, FileText, Globe, Truck, Lock,
  RefreshCw, Loader
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import ConfirmModal from '../../components/common/ConfirmModal';
import { adminAPI, registryAPI, API_URL, getAuthHeaders } from '../../services/api';
import SubcategoryManager from '../../components/wholesaler/SubcategoryManager';

const SERVICE_AREAS = [
  { group: 'Crete', areas: ['heraklion', 'chania', 'rethymno', 'lasithi', 'all_crete'] },
  { group: 'Athens', areas: ['athens_central', 'athens_north', 'athens_south', 'athens_east', 'athens_west', 'piraeus', 'east_attica', 'west_attica', 'all_attica'] },
  { group: 'Other', areas: ['thessaloniki', 'central_macedonia', 'larissa', 'volos', 'patras', 'rhodes', 'corfu', 'mykonos', 'santorini', 'nationwide'] }
];

// ================================================================
// EDIT FORM — Own state, NO inline component defs, plain <input>s
// ================================================================
const WholesalerEditForm = ({ initialData, onSave, onCancel }) => {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(initialData)));
  const [saving, setSaving] = useState(false);
  const [wholesalerTypes, setWholesalerTypes] = useState([]);

  useEffect(() => {
    registryAPI.getWholesalerCategories('en')
      .then(res => setWholesalerTypes(res.data?.data || []))
      .catch(() => {});
  }, []);

  const get = (path) => path.split('.').reduce((o, k) => o?.[k], form) ?? '';

  const set = useCallback((path, value) => {
    setForm(prev => {
      const clone = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let ref = clone;
      for (let i = 0; i < keys.length - 1; i++) { if (!ref[keys[i]]) ref[keys[i]] = {}; ref = ref[keys[i]]; }
      ref[keys[keys.length - 1]] = value;
      return clone;
    });
  }, []);

  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };

  const ic = "w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none";
  const lc = "block text-xs font-medium text-gray-500 mb-1";

  return (
    <div className="space-y-4">
      {/* Company */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Company Information</h2>
        <div className="space-y-3">
          <div><label className={lc}>Company Name</label><input type="text" value={get('companyName')} onChange={e => set('companyName', e.target.value)} className={ic} /></div>
          <div><label className={lc}>Legal Name</label><input type="text" value={get('legalName')} onChange={e => set('legalName', e.target.value)} className={ic} /></div>
          <div><label className={lc}>Business Type</label>
            <select value={get('businessType')} onChange={e => set('businessType', e.target.value)} className={ic}>
              <option value="">Select type...</option>
              {wholesalerTypes.map(type => (
                <option key={type.key} value={type.key}>{type.icon} {type.label}</option>
              ))}
            </select>
          </div>
          <div><label className={lc}>Description</label><textarea value={get('description')} onChange={e => set('description', e.target.value)} rows={2} className={ic + " resize-none"} /></div>
          <div><label className={lc}>Website</label><input type="text" value={get('website')} onChange={e => set('website', e.target.value)} className={ic} /></div>
          <div><label className={lc}>Account Status</label>
            <select value={get('accountStatus') || 'pending'} onChange={e => set('accountStatus', e.target.value)} className={ic}>
              <option value="pending">Pending</option><option value="approved">Approved</option>
              <option value="rejected">Rejected</option><option value="suspended">Suspended</option>
            </select></div>
          <label className="flex items-center gap-2 cursor-pointer pt-1">
            <input type="checkbox" checked={form.privateCatalogMode || false} onChange={e => set('privateCatalogMode', e.target.checked)} className="w-4 h-4 text-blue-600 rounded" />
            <span className="text-sm text-gray-700">Private Catalog Mode</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.simpleOrdersEnabled || false} onChange={e => set('simpleOrdersEnabled', e.target.checked)} className="w-4 h-4 text-sky-600 rounded" />
            <span className="text-sm text-gray-700">Simple Orders Mode</span>
          </label>
        </div>
      </div>

      {/* Fiscal */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Fiscal Information</h2>
        <div className="space-y-3">
          <div><label className={lc}>VAT Number</label><input type="text" value={get('vatNumber')} onChange={e => set('vatNumber', e.target.value)} className={ic} /></div>
          <div><label className={lc}>Tax Office</label><input type="text" value={get('taxOffice')} onChange={e => set('taxOffice', e.target.value)} className={ic} /></div>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Contact</h2>
        <div className="space-y-3">
          <div><label className={lc}>Contact Person</label><input type="text" value={get('contact.contactPerson')} onChange={e => set('contact.contactPerson', e.target.value)} className={ic} /></div>
          <div><label className={lc}>Phone</label><input type="text" value={get('contact.phone')} onChange={e => set('contact.phone', e.target.value)} className={ic} /></div>
          <div><label className={lc}>Email</label><input type="text" value={get('contact.email')} onChange={e => set('contact.email', e.target.value)} className={ic} /></div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Address</h2>
        <div className="space-y-3">
          <div><label className={lc}>Street</label><input type="text" value={get('address.street')} onChange={e => set('address.street', e.target.value)} className={ic} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lc}>City</label><input type="text" value={get('address.city')} onChange={e => set('address.city', e.target.value)} className={ic} /></div>
            <div><label className={lc}>Postal Code</label><input type="text" value={get('address.postalCode')} onChange={e => set('address.postalCode', e.target.value)} className={ic} /></div>
          </div>
          <div><label className={lc}>Country</label><input type="text" value={get('address.country')} onChange={e => set('address.country', e.target.value)} className={ic} /></div>
        </div>
      </div>

      {/* Delivery */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Delivery Settings</h2>
        <div className="space-y-3">
          <div><label className={lc}>Minimum Order (€)</label><input type="number" value={get('deliverySettings.minimumOrder')} onChange={e => set('deliverySettings.minimumOrder', Number(e.target.value))} className={ic} /></div>
          <div><label className={lc}>Delivery Fee (€)</label><input type="number" value={get('deliverySettings.deliveryFee')} onChange={e => set('deliverySettings.deliveryFee', Number(e.target.value))} className={ic} /></div>
          <div><label className={lc}>Free Delivery Above (€)</label><input type="number" value={get('deliverySettings.freeDeliveryThreshold')} onChange={e => set('deliverySettings.freeDeliveryThreshold', Number(e.target.value))} className={ic} /></div>
          <div><label className={lc}>Est. Delivery Days</label><input type="number" value={get('deliverySettings.estimatedDeliveryDays')} onChange={e => set('deliverySettings.estimatedDeliveryDays', Number(e.target.value))} className={ic} /></div>
          <div><label className={lc}>Delivery Notes</label><textarea value={get('deliverySettings.deliveryNotes')} onChange={e => set('deliverySettings.deliveryNotes', e.target.value)} rows={2} className={ic + " resize-none"} /></div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Service Areas</h2>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {SERVICE_AREAS.map(group => (
            <div key={group.group}>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-1">{group.group}</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {group.areas.map(area => {
                  const selected = (form.serviceAreas || []).includes(area);
                  return (
                    <button key={area} type="button" onClick={() => {
                      const cur = form.serviceAreas || [];
                      set('serviceAreas', selected ? cur.filter(a => a !== area) : [...cur, area]);
                    }} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      selected ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                    }`}>{area.replace(/_/g, ' ')}</button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button onClick={handleSave} disabled={saving}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2 text-sm">
          <X className="w-4 h-4" /> Cancel
        </button>
      </div>
    </div>
  );
};

// ================================================================
// VIEW HELPER
// ================================================================
const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0"><p className="text-xs text-gray-400">{label}</p><p className="text-sm font-medium text-gray-900 break-words">{value || 'N/A'}</p></div>
  </div>
);

// ================================================================
// MAIN PAGE
// ================================================================
const AdminWholesalerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [changingTier, setChangingTier] = useState(false);
  const [newTier, setNewTier] = useState('');
  const [customPricing, setCustomPricing] = useState({ enabled: false, monthlyFee: '', perOrderFee: '', notes: '' });
  const [confirmModal, setConfirmModal] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [showSubcatManager, setShowSubcatManager] = useState(false);
  const [erpSyncing, setErpSyncing] = useState(false);
  const [erpSyncResult, setErpSyncResult] = useState(null);

  useEffect(() => { fetchDetail(); }, [id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const r = await adminAPI.getWholesalerDetail(id);
      const d = r.data.data || r.data;
      setData(d);
      setNewTier((d.wholesaler || d).subscriptionTier || 'starter');
      const cp = (d.wholesaler || d).customPricing || {};
      setCustomPricing({
        enabled: cp.enabled || false,
        monthlyFee: cp.monthlyFee != null ? cp.monthlyFee : '',
        perOrderFee: cp.perOrderFee != null ? cp.perOrderFee : '',
        notes: cp.notes || ''
      });
    }
    catch { setError('Failed to load'); } finally { setLoading(false); }
  };

  const handleSave = async (formData) => {
    try { await adminAPI.updateWholesaler(id, formData); setEditing(false); fetchDetail(); }
    catch (err) { toast.error('Failed: ' + (err.response?.data?.message || err.message)); }
  };

  const handleBlockUnblock = async () => {
    const action = data.wholesaler.isActive ? 'Block' : 'Unblock';
    setConfirmModal({
      title: `${action} Wholesaler`,
      message: `${action} this wholesaler?`,
      icon: 'warning',
      confirmLabel: action,
      confirmColor: 'yellow',
      onConfirm: async () => {
        setConfirmModal(null);
        try { await adminAPI.blockUnblockWholesaler(id, { isActive: !data.wholesaler.isActive }); fetchDetail(); } catch { toast.error('Failed'); }
      }
    });
  };

  const handleChangeTier = async () => {
    setConfirmModal({
      title: 'Change Tier',
      message: `Change tier to ${newTier}?`,
      icon: 'info',
      confirmLabel: 'Change',
      confirmColor: 'blue',
      onConfirm: async () => {
        setConfirmModal(null);
        try { await adminAPI.changeWholesalerTier(id, { tier: newTier }); setChangingTier(false); fetchDetail(); } catch { toast.error('Failed'); }
      }
    });
  };

  const handleSaveCustomPricing = async () => {
    setSavingPricing(true);
    try {
      await adminAPI.setCustomPricing(id, {
        enabled: customPricing.enabled,
        monthlyFee: customPricing.monthlyFee !== '' ? Number(customPricing.monthlyFee) : null,
        perOrderFee: customPricing.perOrderFee !== '' ? Number(customPricing.perOrderFee) : null,
        notes: customPricing.notes
      });
      fetchDetail();
    } catch (err) {
      toast.error('Failed to save custom pricing: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingPricing(false);
    }
  };

  const handleErpSync = async () => {
    setErpSyncing(true);
    setErpSyncResult(null);
    try {
      const customerRes = await adminAPI.syncWholesalerErpCustomers(id);
      let discountData = null;
      try {
        const discountRes = await adminAPI.syncWholesalerErpCustomerDiscounts(id);
        discountData = discountRes.data;
      } catch (discErr) {
        discountData = { error: discErr.response?.data?.message || discErr.message };
      }
      setErpSyncResult({ customers: customerRes.data, discounts: discountData });
      toast.success('ERP sync complete');
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setErpSyncResult({ error: msg });
      toast.error('ERP sync failed: ' + msg);
    } finally {
      setErpSyncing(false);
    }
  };

  const getTierColor = (t) => ({ starter: 'bg-gray-100 text-gray-800 border-gray-300', professional: 'bg-blue-100 text-blue-800 border-blue-300', enterprise: 'bg-purple-100 text-purple-800 border-purple-300' }[t] || 'bg-gray-100 text-gray-800 border-gray-300');
  const getTierInfo = (t) => ({ starter: { limit: '1,500', fee: '€0.40', price: '€49' }, professional: { limit: '5,000', fee: '€0.25', price: '€129' }, enterprise: { limit: 'Unlimited', fee: '€0.15', price: '€299' } }[t] || { limit: '1,500', fee: '€0.40', price: '€49' });

  if (loading) return <div className="min-h-screen bg-gray-50"><Navbar /><div className="flex justify-center items-center h-64 text-gray-500">Loading...</div><Footer /></div>;
  if (error || !data) return <div className="min-h-screen bg-gray-50"><Navbar /><div className="container mx-auto px-4 py-8 pt-16 lg:pt-8"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error || 'Not found'}</div></div><Footer /></div>;

  const { wholesaler, stats, recentOrders } = data;
  const tierInfo = getTierInfo(wholesaler.subscriptionTier);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8 max-w-6xl">
        <div className="mb-6">
          <button onClick={() => navigate('/admin/wholesalers')} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Wholesalers</button>
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{wholesaler.companyName}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${wholesaler.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{wholesaler.isActive ? 'Active' : 'Blocked'}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${getTierColor(wholesaler.subscriptionTier)}`}>{wholesaler.subscriptionTier}</span>
                {wholesaler.privateCatalogMode && <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1"><Lock className="w-3 h-3" /> Private</span>}
                <span className="text-xs text-gray-400">Registered: {wholesaler.createdAt ? format(new Date(wholesaler.createdAt), 'MMM dd, yyyy') : 'N/A'}</span>
              </div>
            </div>
            {!editing && (
              <div className="flex gap-2">
                <button onClick={() => setEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"><Edit className="w-4 h-4" /> Edit</button>
                <button onClick={handleBlockUnblock} className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm ${wholesaler.isActive ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                  {wholesaler.isActive ? <><Ban className="w-4 h-4" /> Block</> : <><CheckCircle className="w-4 h-4" /> Unblock</>}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Total Orders</p><p className="text-2xl font-bold text-gray-900">{stats?.totalOrders || 0}</p></div>
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Revenue</p><p className="text-2xl font-bold text-green-600">€{(stats?.totalRevenue || 0).toFixed(2)}</p></div>
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Products</p><p className="text-2xl font-bold text-blue-600">{stats?.productCount || 0}</p></div>
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Customers</p><p className="text-2xl font-bold text-purple-600">{stats?.uniqueBusinesses || 0}</p></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            {editing ? (
              <WholesalerEditForm key={wholesaler._id} initialData={wholesaler} onSave={handleSave} onCancel={() => setEditing(false)} />
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Company</h2>
                  <InfoRow icon={Building} label="Company" value={wholesaler.companyName} />
                  <InfoRow icon={FileText} label="Legal Name" value={wholesaler.legalName} />
                  <InfoRow icon={Building} label="Type" value={wholesaler.businessType} />
                  <InfoRow icon={Globe} label="Website" value={wholesaler.website} />
                  <InfoRow icon={Calendar} label="Registered" value={wholesaler.createdAt ? format(new Date(wholesaler.createdAt), 'MMMM dd, yyyy') : 'N/A'} />
                  {wholesaler.description && <InfoRow icon={FileText} label="Description" value={wholesaler.description} />}</div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Fiscal</h2>
                  <InfoRow icon={Hash} label="VAT" value={wholesaler.vatNumber} />
                  <InfoRow icon={FileText} label="Tax Office" value={wholesaler.taxOffice} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Modes</h2>
                  <InfoRow icon={Lock} label="Private Catalog" value={wholesaler.privateCatalogMode ? 'On' : 'Off'} />
                  <InfoRow icon={CheckCircle} label="Simple Orders" value={wholesaler.simpleOrdersEnabled ? 'On' : 'Off'} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Contact</h2>
                  <InfoRow icon={Building} label="Person" value={wholesaler.contact?.contactPerson || wholesaler.contactPerson} />
                  <InfoRow icon={Phone} label="Phone" value={wholesaler.contact?.phone} />
                  <InfoRow icon={Mail} label="Email" value={wholesaler.contact?.email || wholesaler.email} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Address</h2>
                  <InfoRow icon={MapPin} label="Street" value={wholesaler.address?.street} />
                  <InfoRow icon={MapPin} label="City/Postal" value={`${wholesaler.address?.city || ''} ${wholesaler.address?.postalCode || ''}`} />
                  <InfoRow icon={Globe} label="Country" value={wholesaler.address?.country} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Delivery</h2>
                  <InfoRow icon={Truck} label="Min Order" value={`€${wholesaler.deliverySettings?.minimumOrder || 0}`} />
                  <InfoRow icon={Truck} label="Fee" value={`€${wholesaler.deliverySettings?.deliveryFee || 0}`} />
                  <InfoRow icon={Truck} label="Free Above" value={wholesaler.deliverySettings?.freeDeliveryThreshold ? `€${wholesaler.deliverySettings.freeDeliveryThreshold}` : 'N/A'} />
                  <InfoRow icon={Truck} label="Est. Days" value={wholesaler.deliverySettings?.estimatedDeliveryDays || '1'} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Service Areas</h2>
                  <div className="flex flex-wrap gap-1.5">
                    {(wholesaler.serviceAreas || []).length > 0 ? wholesaler.serviceAreas.map(a => (
                      <span key={a} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">{a.replace(/_/g, ' ')}</span>
                    )) : <span className="text-sm text-gray-400">No areas set</span>}
                  </div></div>
                <div className="bg-white rounded-lg shadow-sm border p-5">
                  <div className="flex justify-between items-center mb-3"><h2 className="text-sm font-semibold text-gray-900">Subscription</h2>
                    {!changingTier && <button onClick={() => setChangingTier(true)} className="text-xs text-blue-600 hover:text-blue-700">Change</button>}</div>
                  {changingTier ? (
                    <div className="space-y-3">
                      <select value={newTier} onChange={e => setNewTier(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                        <option value="starter">Starter — €49/mo — 1,500 products</option>
                        <option value="professional">Professional — €129/mo — 5,000 products</option>
                        <option value="enterprise">Enterprise — €299/mo — Unlimited</option>
                      </select>
                      <div className="flex gap-2">
                        <button onClick={handleChangeTier} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm">Confirm</button>
                        <button onClick={() => { setChangingTier(false); setNewTier(wholesaler.subscriptionTier); }} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className={`p-4 rounded-lg border-2 ${getTierColor(wholesaler.subscriptionTier)}`}>
                      <div className="flex items-center justify-between mb-2"><span className="text-base font-bold capitalize">{wholesaler.subscriptionTier}</span><Award className="w-5 h-5" /></div>
                      <div className="space-y-1 text-sm">
                        <p>Price: <strong>{tierInfo.price}/mo</strong></p>
                        <p>Product Limit: <strong>{tierInfo.limit}</strong></p>
                        <p>Per-Order Fee: <strong>{tierInfo.fee}</strong></p>
                        <p>Products: <strong>{stats?.productCount || 0}</strong></p>
                        <p>Billing: <strong className="capitalize">{wholesaler.billingCycle || 'monthly'}</strong></p>
                      </div>
                    </div>
                  )}
                </div>
                {/* Custom Billing Card */}
                <div className="bg-white rounded-lg shadow-sm border p-5">
                  <div className="flex justify-between items-center mb-3">
                    <h2 className="text-sm font-semibold text-gray-900">Custom Billing</h2>
                    {wholesaler.customPricing?.enabled && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">Active</span>
                    )}
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={customPricing.enabled}
                        onChange={e => setCustomPricing(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded" />
                      <span className="text-sm text-gray-700">Enable Custom Pricing</span>
                    </label>
                    {customPricing.enabled && (
                      <>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Monthly Fee Override</label>
                          <input type="number" step="0.01" min="0"
                            placeholder={tierInfo.price.replace('€', '')}
                            value={customPricing.monthlyFee}
                            onChange={e => setCustomPricing(prev => ({ ...prev, monthlyFee: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Per-Order Fee Override</label>
                          <input type="number" step="0.01" min="0"
                            placeholder={tierInfo.fee.replace('€', '')}
                            value={customPricing.perOrderFee}
                            onChange={e => setCustomPricing(prev => ({ ...prev, perOrderFee: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Notes (reason for deal)</label>
                          <textarea rows={2} value={customPricing.notes}
                            onChange={e => setCustomPricing(prev => ({ ...prev, notes: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                            placeholder="e.g. Volume discount, partner deal..." />
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
                          <p className="font-medium mb-1">Effective Pricing:</p>
                          <p>Monthly: €{customPricing.monthlyFee || tierInfo.price.replace('€', '')}/mo</p>
                          <p>Per-order: €{customPricing.perOrderFee || tierInfo.fee.replace('€', '')}</p>
                        </div>
                      </>
                    )}
                    <button onClick={handleSaveCustomPricing} disabled={savingPricing}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                      {savingPricing ? 'Saving...' : 'Save Pricing'}
                    </button>
                  </div>
                </div>
                {wholesaler.erpIntegration?.isActive && (
                  <div className="bg-white rounded-lg shadow-sm border p-5">
                    <div className="flex justify-between items-center mb-3">
                      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-purple-600" /> ERP Sync
                      </h2>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Enterprise</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Pull customer ERP codes (VAT→TRDR) and customer discount categories (PRCCATEGORY) from SoftOne for this wholesaler.</p>
                    <button
                      onClick={handleErpSync}
                      disabled={erpSyncing}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {erpSyncing ? <><Loader className="w-4 h-4 animate-spin" /> Syncing...</> : <>Sync Now</>}
                    </button>
                    {erpSyncResult && (
                      <div className={`mt-3 p-3 rounded-lg text-xs ${erpSyncResult.error ? 'bg-red-50 border border-red-200 text-red-700' : (erpSyncResult.discounts?.error ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' : 'bg-green-50 border border-green-200 text-green-700')}`}>
                        {erpSyncResult.error ? (
                          <p>{erpSyncResult.error}</p>
                        ) : (
                          <div className="space-y-2">
                            {erpSyncResult.customers && (
                              <div>
                                <p className="font-semibold">Customers synced</p>
                                <p>Total: {erpSyncResult.customers.total} · Matched: {erpSyncResult.customers.matched} · Updated: {erpSyncResult.customers.customersUpdated}</p>
                              </div>
                            )}
                            {erpSyncResult.discounts && !erpSyncResult.discounts.error && (
                              <div className="border-t border-green-200 pt-2">
                                <p className="font-semibold">Discounts synced</p>
                                <p>Categories: {erpSyncResult.discounts.prccategories} · Businesses: {erpSyncResult.discounts.totalBusinesses} · Created: {erpSyncResult.discounts.created} · Updated: {erpSyncResult.discounts.updated}{erpSyncResult.discounts.skipped > 0 ? ` · No discount: ${erpSyncResult.discounts.skipped}` : ''}</p>
                              </div>
                            )}
                            {erpSyncResult.discounts?.error && (
                              <div className="border-t border-yellow-200 pt-2">
                                <p className="font-semibold">Discount sync failed</p>
                                <p>{erpSyncResult.discounts.error}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-6">
            {/* Subcategory Management */}
            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-900">Subcategories</h2>
                <button
                  onClick={() => setShowSubcatManager(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
                >
                  Manage Subcategories
                </button>
              </div>
              <p className="text-xs text-gray-500">
                {(wholesaler.customSubcategories || []).length} custom subcategories defined
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex justify-between items-center mb-4"><h2 className="text-sm font-semibold text-gray-900">Recent Orders</h2><span className="text-xs text-gray-400">{stats?.totalOrders || 0} total</span></div>
              {(!recentOrders || recentOrders.length === 0) ? (
                <div className="text-center py-12"><ShoppingCart className="w-12 h-12 text-gray-200 mx-auto mb-3" /><p className="text-gray-400 text-sm">No orders yet</p></div>
              ) : (
                <div className="space-y-2">{recentOrders.slice(0, 10).map(order => (
                  <div key={order._id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50">
                    <div><div className="flex items-center gap-2 mb-0.5"><span className="text-sm font-medium text-gray-900">{order.orderNumber}</span>
                      <span className={`px-2 py-0.5 text-xs rounded capitalize ${order.status === 'delivered' ? 'bg-green-100 text-green-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{order.status}</span></div>
                      <p className="text-xs text-gray-500">{order.businessId?.businessName || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">{order.createdAt ? format(new Date(order.createdAt), 'MMM dd, yyyy h:mm a') : ''}</p></div>
                    <div className="text-right"><p className="text-sm font-bold text-gray-900">€{(order.totals?.total || 0).toFixed(2)}</p><p className="text-xs text-gray-400">{order.items?.length || 0} items</p></div>
                  </div>))}</div>)}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <SubcategoryManager
        isOpen={showSubcatManager}
        onClose={() => setShowSubcatManager(false)}
        onSubcategoriesChanged={fetchDetail}
        adminMode={true}
        wholesalerId={id}
      />
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminWholesalerDetail;